import { useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../authContext/useAuth';
import { useToast } from '../context/ToastContext';

interface IdleTimerConfig {
  timeout: number;
  warningTimeout: number;
  onWarning?: () => void;
  onIdle?: () => void;
  events?: string[];
}

const DEFAULT_EVENTS = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];

export const useIdleTimer = (config: IdleTimerConfig) => {
  const { logout } = useAuth();
  const { showToast } = useToast();
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const warningTimerRef = useRef<NodeJS.Timeout | null>(null);
  const isWarningShownRef = useRef(false);
  const lastActivityRef = useRef<number>(Date.now());

  const {
    timeout = 30 * 60 * 1000,
    warningTimeout = 29 * 60 * 1000,
    onWarning,
    onIdle,
    events = DEFAULT_EVENTS,
  } = config;

  const resetTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    if (warningTimerRef.current) {
      clearTimeout(warningTimerRef.current);
    }

    lastActivityRef.current = Date.now();
    isWarningShownRef.current = false;

    // Set warning timer
    warningTimerRef.current = setTimeout(() => {
      if (!isWarningShownRef.current) {
        isWarningShownRef.current = true;
        if (onWarning) {
          onWarning();
        } else {
          showToast(
            'Your session will expire in 1 minute due to inactivity. Please click anywhere to stay logged in.',
            'warning',
          );
        }
      }
    }, warningTimeout);

    // Set idle timer
    timerRef.current = setTimeout(async () => {
      if (onIdle) {
        onIdle();
      } else {
        showToast('You have been logged out due to inactivity.', 'info');
        await logout();
      }
    }, timeout);
  }, [timeout, warningTimeout, onWarning, onIdle, logout, showToast]);

  const handleActivity = useCallback(() => {
    resetTimer();
  }, [resetTimer]);

  useEffect(() => {
    events.forEach((event) => {
      document.addEventListener(event, handleActivity, true);
    });

    resetTimer();

    return () => {
      events.forEach((event) => {
        document.removeEventListener(event, handleActivity, true);
      });

      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
      if (warningTimerRef.current) {
        clearTimeout(warningTimerRef.current);
      }
    };
  }, [events, handleActivity, resetTimer]);

  const getRemainingTime = useCallback(() => {
    const elapsed = Date.now() - lastActivityRef.current;
    return Math.max(0, timeout - elapsed);
  }, [timeout]);

  const getLastActivity = useCallback(() => {
    return lastActivityRef.current;
  }, []);

  const isIdle = useCallback(() => {
    return getRemainingTime() === 0;
  }, [getRemainingTime]);

  return {
    resetTimer,
    getRemainingTime,
    getLastActivity,
    isIdle,
  };
};
